<template lang="pug">
#recruitment-contact-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 入所募集お問い合わせの代理登録

    .box-body.register-body
      //- contactDatetime
      FormDatetimePicker(v-model="contactDatetime" :date.sync="contactDatetimeDate" :time.sync="contactDatetimeTime"
        name="application-datetime" key-name="お問い合わせ日時" placeholder="お問い合わせ日時" :validation-rules="{ required: true }" required)
      //- done
      .d-flex.flex-column.mb-3(style="margin-left:20px;")
        span.done-name 対応状態
        SupportDoneSwitch(v-model="done")
      //- name
      FormInput(v-model="name" key-name="氏名" name="name" type="text" placeholder="氏名" :maxlength="32"
        :validation-rules="{ required: true, max: 32 }" :styles="{ width: '340px' }" required)
      //- tel
      FormInput(v-model="tel" key-name="電話番号" name="tel" type="text" placeholder="電話番号" :maxlength="13"
        :validation-rules="{ required: true, tel: true, max: 14 }" :styles="{ width: '340px' }" required)
      //- contactMessage
      FormTextarea(v-model="contactMessage" key-name="お問い合わせ内容" name="applicant-message" placeholder="お問い合わせ内容" :maxlength="1000" :rows="7"
        :validation-rules="{ max: 1000 }" :styles="{ maxWidth: '640px', width: '640px' }")
      //- customerName
      FormInput(v-model="customerName" key-name="担当者" name="customerName" type="text" placeholder="担当者" :maxlength="100"
        :validation-rules="{ max: 100 }")
      //- supportMemo
      FormTextarea(v-model="supportMemo" key-name="対応メモ" name="reception-memo" placeholder="対応メモ" :maxlength="1000" :rows="7"
        :validation-rules="{ max: 1000 }" :styles="{ maxWidth: '640px', width: '640px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="onRegister" type="button") 登録
        button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  OkCancelModal(v-model="showRegisterConfirmModal" title="入所募集問い合わせ情報を登録しますか？" @ok="registerRecruitmentContact")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoRecruitmentContactList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import CustomValidate from "@/commons/utils/CustomValidate";

import RecruitmentContactsService, { ContactType, RecruitmentContactsPostModel } from "@/commons/services/RecruitmentContacts";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import SupportDoneSwitch from "@/pc/basics/SupportDoneSwitch.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

/**
 * ## 入所募集問い合わせ登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    SupportDoneSwitch,
    FormDatetimePicker,
    FormInput,
    FormTextarea
  },
  $_veeValidate: { validator: "new" }
})
export default class RecruitmentContactRegister extends Vue {
  /** 入所募集問い合わせサービス */
  @Inject("RecruitmentContactsService") recruitmentContactsService!: RecruitmentContactsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterConfirmModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** お問い合わせ日時 */
  contactDatetime: number | null = null;
  /** お問い合わせ日時(日付) */
  contactDatetimeDate: string = "";
  /** お問い合わせ日時(開始時刻) */
  contactDatetimeTime: string = "";
  /** 対応状態 */
  done: boolean = false;
  /** 氏名 */
  name: string = "";
  /** 電話番号 */
  tel: string = "";
  /** お問い合わせ内容 */
  contactMessage: string = "";
  /** 担当者 */
  customerName: string = "";
  /** 入所募集問い合わせ、対応メモ */
  supportMemo: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  get _requiredDatetime(): boolean {
    return this.done;
  }

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - お問い合わせ日時はカレンダー上の初期値を作成する
   */
  public createView(): void {
    const contactDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const contactDatetimeTime: string = "10:00:00";
    this.contactDatetimeDate = contactDatetimeDate;
    this.contactDatetimeTime = contactDatetimeTime;

    return;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async onRegister(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterConfirmModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 入所募集問い合わせを登録する
   */
  public async registerRecruitmentContact(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const requestBody: RecruitmentContactsPostModel = this.createRequestBody();
    await this.recruitmentContactsService.post(userStore, requestBody);

    this.showRegisterSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 入所募集問い合わせ登録のリクエストボディを作成する
   */
  public createRequestBody(): RecruitmentContactsPostModel {
    return {
      contactDatetime: this.contactDatetime as number,
      contactType: ContactType.FACILITY,
      done: this.done,
      name: this.name,
      tel: this.tel,
      contactMessage: this.contactMessage ? this.contactMessage : null,
      customerName: this.customerName ? this.customerName : null,
      supportMemo: this.supportMemo ? this.supportMemo : null
    };
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.gotoRecruitmentContactList();
    return;
  }

  /**
   * ### 入所募集問い合わせ一覧に遷移する
   */
  public gotoRecruitmentContactList(): void {
    this.$router.push(PcUrl.RecruitmentContactList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#recruitment-contact-register {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .done-name {
    height: 27px;
    margin-bottom: 4px;
    font-size: 18px;
    font-weight: bold;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
